import React from 'react';
import { useTranslation } from 'react-i18next';

import { GlobeIcon, CheckIcon } from '../icons';
import { Field, Globe, Input, Check } from './ConnectDomainField.styles';

export interface ConnectDomainFieldProps {
  value: string;
  /** Omit / pair with `readOnly` for the static "chosen domain" variant. */
  onChange?: (value: string) => void;
  /** Fired on Enter (desktop submit shortcut). */
  onSubmit?: () => void;
  /** True once the value looks like a domain — reveals the green tick. */
  valid: boolean;
  variant: 'desktop' | 'mobile';
  /** Placeholder copy (routed through i18n). */
  placeholder?: string;
  readOnly?: boolean;
}

/**
 * The "type your own domain" field. Desktop is a large light input (no leading
 * icon); mobile is a dark input with a leading globe. A green tick appears on the
 * right once the value validates. Also used read-only to show the chosen domain.
 */
export const ConnectDomainField = ({
  value,
  onChange,
  onSubmit,
  valid,
  variant,
  placeholder,
  readOnly = false,
}: ConnectDomainFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  // Translate the caller's placeholder key, or fall back to the default copy
  // (kept as a literal here so it reaches the catalog and stays reactive).
  const placeholderText = placeholder ? t(placeholder) : t('Type your own domain name here');

  return (
    <Field $variant={variant}>
      {variant === 'mobile' ? (
        <Globe $variant={variant}>
          <GlobeIcon size={18} />
        </Globe>
      ) : null}
      <Input
        $variant={variant}
        type="text"
        inputMode="url"
        autoComplete="off"
        spellCheck={false}
        readOnly={readOnly}
        value={value}
        placeholder={placeholderText}
        aria-label={placeholderText}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onKeyDown={
          onSubmit
            ? (e) => {
                if (e.key === 'Enter' && valid) onSubmit();
              }
            : undefined
        }
      />
      {valid ? (
        <Check>
          <CheckIcon size={13} />
        </Check>
      ) : null}
    </Field>
  );
};

export default ConnectDomainField;